/** @jsx jsx */
import { jsx } from '@emotion/react';

import { createContext, ReactNode, useMemo } from 'react';
import {
	axisBottom,
	axisLeft,
	max,
	scaleLinear,
	ScaleLinear,
	ScaleOrdinal,
	scaleOrdinal,
	ScaleTime,
	scaleUtc,
	select,
	stackOrderNone,
	sum,
} from 'd3';
import { HistogramEndpointMinorBin, HistogramEndpointResponse } from 'srs-middleware';
import { StackedHistogramEndpointResponse, StackedHistogramEndpointMinorBin } from '../../widgets/endpoints';
import { useRef } from 'react';
import { MutableRefObject } from 'react';
import { ColorAliases } from 'styles/variables';
import { StackOrder } from 'widgets/timeline';
import { useEffect } from 'react';
import { SMALL_STYLE_WIDTH } from 'components/legendV2';

const _axisPadding = {
	y: 30,
	x: 25,
};

export type SRSTimeScale = ScaleTime<number, number>;

export interface ChartContext {
	dataHeight: number;
	dataWidth: number;
	margin: { top: number; left: number; bottom: number; right: number };
	data: FormattedChildBin[];
	svgRef: MutableRefObject<SVGSVGElement>;
	keys: string[];
	xScale: SRSTimeScale;
	yScale: ScaleLinear<number, number>;
	colorScale: ScaleOrdinal<string, string, never>;
	stackOrder?: StackOrder;
}

interface TimelineChartProps {
	srsHistogramData: HistogramEndpointResponse | StackedHistogramEndpointResponse;
	dimensions: {
		height: number;
		width: number;
	};
	children: ReactNode;
	margin?: {
		top?: number;
		left?: number;
		right?: number;
		bottom?: number;
	};
	yDomain?: [number, number];
	cropEmptyExtents?: boolean;
	stacked?: boolean;
	colorScale?: any;
	stackOrder?: StackOrder;
	tickSpacing?: number;
	hideLabels?: boolean;
	xAxisLabel?: string;
	yAxisLabel?: string;
	legend?: boolean;
}

export const TimelineContext = createContext<ChartContext>(null);

export function TimelineChart({
	srsHistogramData,
	dimensions,
	children,
	margin: marginProp = {},
	yDomain: yDomainOverride,
	cropEmptyExtents = false,
	stacked,
	colorScale,
	stackOrder = stackOrderNone,
	hideLabels,
	xAxisLabel,
	yAxisLabel,
	legend
}: TimelineChartProps) {
	const { height, width } = dimensions;
	const axisPadding = useMemo(() => {
		const axisPadding = {..._axisPadding};
		if(hideLabels) {
			axisPadding.y = 0;
			axisPadding.x = 0;
		}
		return axisPadding;
	}, [hideLabels]);

	const { margin, dataWidth, dataHeight } = useMemo(() => {
		const defaultMargin = {
			top: 15,
			left: 0,
			bottom: 0,
			right: 20,
		};

		const margin = { ...defaultMargin, ...marginProp };
		const dataWidth = width - axisPadding.y - margin.right;
		const dataHeight = height - axisPadding.x - margin.top;

		const chartDimensions = { margin, dataWidth, dataHeight };

		return chartDimensions;
	}, [height, width, marginProp]);

	// Create chart container
	const svgRef = useRef<SVGSVGElement>(null);

	// Convert SRS timeline-library data into separate pieces
	const data = useMemo(() => formatSRSTimeline(srsHistogramData, stacked), [srsHistogramData, stacked]);

	// Scales
	const { xScale, yScale } = useMemo(() => {
		let legendOffset = legend ? SMALL_STYLE_WIDTH: 0;
		const scales = {
			//Since dataWidth has axisPadding.y subtracted, we need to offset it here since this scale starts at axisPadding.y.
			//Otherwise: 
			//vizWidth = dataWidth - axisPadding.y;
			// dataWidth = widgetWidth - axisPadding.y - marginRight
			// SOOOOO we substitute and we get
			//vizWidth = (widgetWidth - axisPadding.y - marginRight) - axisPadding.y
			//vizWidth = widgetWith - 2*axisPadding.y - marginRight. Which is less than what we want
			//So add axisPadding.y to the range and its all right. DataWidth used other places so we can't just edit that
			xScale: scaleUtc().domain([]).range([axisPadding.y, dataWidth + axisPadding.y - legendOffset]),
			yScale: scaleLinear()
				.domain(yDomainOverride ?? [])
				.range([dataHeight, 0]),
		};

		// Scales
		if (data.length >= 1) {
			let first, last;

			// X-domain
			if (cropEmptyExtents) {
				const predicate = (d: FormattedChildBin) => sum(Object.values(d.values)) > 0;
				first = data.find(predicate) ?? data[0];
				last = Array.from(data).reverse().find(predicate) ?? data[data.length - 1];
			} else {
				first = data[0];
				last = data[data.length - 1];
			}

			// Y domain
			const maxY = max(data, (d) => sum(Object.values(d.values)));

			scales.xScale.domain([first.start, last.end]);
			scales.yScale.domain(yDomainOverride ?? [0, maxY === 0 ? 1 : maxY]);
		}

		return scales;
	}, [axisPadding, data, dataHeight, dataWidth, yDomainOverride, cropEmptyExtents]);

	// Create chart axis'
	useEffect(() => {
		// We can be smarter about how many ticks to show, but this will work
		const xAxis = axisBottom(xScale).ticks(dataWidth / 150);
		const yAxis = axisLeft(yScale).ticks(dataHeight / 50, '~s');

		select(svgRef.current).selectAll('g.xAxis,g.xAxis-label').remove();
		if(!hideLabels) {
			select(svgRef.current)
				.append('g')
				.classed('xAxis', true)
				.attr('transform', `translate(0, ${dataHeight + margin.top})`)
				.call(xAxis);
			if(xAxisLabel) {
				select(svgRef.current)
					.append('g')
					.classed('xAxis-label', true)
					.attr('transform', `translate(${axisPadding.y + dataWidth / 2}, ${dataHeight + margin.top + 35})`)
					.append("text")
					.text(xAxisLabel);
			}
		}

		select(svgRef.current).selectAll('g.yAxis,g.yAxis-label').remove();
		if(!hideLabels) {
			select(svgRef.current)
				.append('g')
				.classed('yAxis', true)
				.attr('transform', `translate(${axisPadding.y}, ${margin.top})`)
				.call(yAxis);
			if(yAxisLabel) {
				select(svgRef.current)
					.append('g')
					.classed('yAxis-label', true)
					.attr('transform', `rotate(-90) translate(-${dataHeight/2 + axisPadding.y + 5}, 0)`)
					.append("text")
					.style("text-anchor", "middle")
					.text(yAxisLabel);
			}
		}
	}, [dataHeight, dataWidth, margin.top, xScale, yScale, hideLabels]);

	const chartColorScale = useMemo(
		() =>
			stacked
				? colorScale
				: scaleOrdinal<string, string, never>().domain(['Count']).range([ColorAliases.PT_INTENT_PRIMARY]),
		[stacked, colorScale]
	);

	const keys = useMemo(
		() => (stacked ? (srsHistogramData as StackedHistogramEndpointResponse).keys : ['Count']),
		[stacked, srsHistogramData]
	);

	const chartContext: ChartContext = {
		dataHeight,
		dataWidth,
		margin,
		data,
		svgRef,
		keys, // This could be changed in the endpoint request so that the data is transformed to match a stacked call
		xScale,
		yScale,
		colorScale: chartColorScale,
		stackOrder,
	};

	return (
		<svg cy-test="timeline-data" ref={svgRef} width={width} height={height} style={{overflow: "visible"}}>
			<TimelineContext.Provider value={chartContext}>{children}</TimelineContext.Provider>
		</svg>
	);
}

export interface FormattedParentBin {
	start: Date;
	end: Date;
	label: string;
}

export interface FormattedChildBin extends FormattedParentBin {
	values: Record<string, number>;
}

export const formatSRSTimeline = (
	timeline: HistogramEndpointResponse | StackedHistogramEndpointResponse,
	stacked = false
): FormattedChildBin[] => {
	const timelineBins = timeline?.bins;

	const rootData = (timelineBins as any[])?.flatMap((bin) => bin.binData) ?? [];

	/* Is this still neccessary? */
	if ((timelineBins as any[])?.filter((bin) => !!bin).length === 0) {
		console.error('!!! SRS deployment out of date. Redeploy SRS to fix binning by YEAR bug!');
	}

	if (stacked) {
		return (rootData as StackedHistogramEndpointMinorBin[])
			.filter((data) => !!data)
			.map(({ counts, label, end, start }) => {
				const zeroedValues: [string, number][] = (timeline as StackedHistogramEndpointResponse).keys.map((d) => [
					d,
					counts[d] || 0,
				]);

				const topTenNonZeroValues = zeroedValues
					.sort((a, b) => b[1] - a[1])
					.slice(0, 10)
					.reduce((acc, cur) => {
						if (cur[1] > 0) {
							acc.push(cur);
						}
						return acc;
					}, []);

				return {
					values: Object.fromEntries(topTenNonZeroValues),
					label,
					end: new Date(end),
					start: new Date(start),
				};
			});
	} else {
		return (rootData as HistogramEndpointMinorBin[])
			.filter((data) => !!data)
			.map(({ count, label, end, start }) => ({
				values: { Count: count },
				label,
				end: new Date(end),
				start: new Date(start),
			}));
	}
};
